'use client';

import InlineStyle from '@/components/InlineStyle';
import { useEffect, useRef, useState } from 'react';
import { reportFormError } from '@/lib/formMonitor';
import { hashUser } from '@/lib/hashUser';
import { HS_BOOK_DEMO_FORM_ID, HS_PORTAL_ID } from '@/lib/hubspotForms';
import { preloadHubSpotForms } from '@/lib/preloadHubSpotForms';
import { useDeferredVisible } from '@/lib/useDeferredVisible';
import { watchHubSpotLightFormTheme } from '@/lib/hubspotLightFormTheme';
import { useTranslation } from "@/hooks/useTranslation";
import styles from './GatedResourceForm.module.css';

export default function GatedResourceForm({
  resourceType,
  formId,
  redirectUrl,
  resourceName = 'Resource'
}) {
  const {
    t
  } = useTranslation();
  // Same HubSpot embed as homepage BookDemoSection for consistent fields + styling.
  const activeFormId = formId || HS_BOOK_DEMO_FORM_ID;
  const divRef = useRef(null);
  const wrapRef = useRef(null);
  const formId_ref = useRef(`hs-form-${(activeFormId || resourceType || 'default').replace(/[^a-z0-9]/gi, '-').slice(0, 20)}`);
  const loaded = useRef(false);
  const themeCleanup = useRef(null);
  const [status, setStatus] = useState('idle');
  const [countdown, setCountdown] = useState(3);
  const isVideo = redirectUrl && (redirectUrl.includes('youtube') || redirectUrl.includes('youtu.be') || redirectUrl.includes('vimeo'));
  // Lead page — load HubSpot immediately (no scroll deferral).
  const shouldLoad = useDeferredVisible(wrapRef, { eager: true });

  useEffect(() => {
    preloadHubSpotForms();
  }, []);
  useEffect(() => {
    if (!shouldLoad) return;
    if (loaded.current || !activeFormId) return;
    loaded.current = true;
    setStatus('loading');
    function mountHSForm() {
      if (!divRef.current || !window.hbspt) return;
      try {
        window.hbspt.forms.create({
          portalId: HS_PORTAL_ID,
          formId: activeFormId,
          target: `#${formId_ref.current}`,
          cssClass: 'lb-hs-form',
          onFormReady: () => {
            setStatus('ready');
            themeCleanup.current?.();
            themeCleanup.current = watchHubSpotLightFormTheme(divRef.current);
          },
          onFormSubmitted: (form, data) => {
            setStatus('submitted');

            // ── GA4 event ──
            if (window.gtag) {
              window.gtag('event', 'resource_download', {
                event_category: 'Lead',
                event_label: resourceName,
                resource_type: resourceType,
                value: 1
              });
            }

            // ── Enhanced Conversions via GTM (leadDataOnThankYou) ──
            // Hash PII client-side using Web Crypto API (replaces /api/hash-user
            // which doesn't work on cPanel static hosts). Hashed values go into
            // GTM dataLayer for Enhanced Conversions / Customer Match.
            try {
              const email = data?.submissionValues?.email || '';
              const phone = data?.submissionValues?.phone || data?.submissionValues?.phone_number || '';
              if (email || phone) {
                hashUser({
                  email,
                  phone
                }).then(({
                  hashed_email,
                  hashed_phone
                }) => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'leadDataOnThankYou',
                    leadsUserData: {
                      email: hashed_email || '',
                      phone_number: hashed_phone || ''
                    }
                  });
                }).catch(() => {});
              }
            } catch (e) {}
            if (redirectUrl) {
              let count = 1;
              setCountdown(count);
              const timer = setInterval(() => {
                count--;
                setCountdown(count);
                if (count <= 0) {
                  clearInterval(timer);
                  window.open(redirectUrl, '_blank');
                }
              }, 1000);
            }
          },
          onFormSubmitError: err => {
            setStatus('error');
            reportFormError({
              form: `${resourceType}-${resourceName}`,
              error: JSON.stringify(err),
              errorType: 'submit_failure'
            });
          }
        });
      } catch (err) {
        setStatus('error');
        reportFormError({
          form: resourceType,
          error: err.message,
          errorType: 'load_failure'
        });
      }
    }
    if (window.hbspt) {
      mountHSForm();
    } else {
      const s = document.createElement('script');
      s.src = 'https://js.hsforms.net/forms/v2.js';
      s.defer = true;
      s.onload = mountHSForm;
      s.onerror = () => {
        setStatus('error');
        reportFormError({
          form: resourceType,
          error: 'HS script blocked',
          errorType: 'script_blocked'
        });
      };
      document.head.appendChild(s);
    }
    return () => {
      themeCleanup.current?.();
      themeCleanup.current = null;
    };
  }, [shouldLoad, activeFormId, redirectUrl, resourceName, resourceType]);

  /* ── Submitted ── */
  if (status === 'submitted') {
    return <div style={{
      textAlign: 'center',
      padding: '32px 16px'
    }}>
        <div style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: '#ECFDF5',
        border: '2px solid #10B981',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px'
      }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
        <h3 style={{
        fontFamily: "'Inter Tight',sans-serif",
        fontWeight: 700,
        fontSize: 19,
        color: '#0D1117',
        marginBottom: 8
      }}>
          {t("gated.youre_all_set")}
        </h3>
        <p style={{
        fontSize: 14,
        color: '#64748B',
        lineHeight: 1.7,
        fontFamily: "'Inter Tight',sans-serif",
        marginBottom: 20
      }}>
          {redirectUrl ? `Opening ${isVideo ? 'webinar' : 'your resource'} in ${countdown} second${countdown !== 1 ? 's' : ''}…` : 'Our team will be in touch within 24 hours.'}
        </p>
        {redirectUrl && <a href={redirectUrl} target="_blank" rel="noopener noreferrer" style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '11px 24px',
        borderRadius: 100,
        background: 'linear-gradient(135deg,#0098FF,#0057D4)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
        fontFamily: "'Inter Tight',sans-serif"
      }}>
            {isVideo ? '▶ Watch now' : '⬇ Download now'}
          </a>}
      </div>;
  }

  /* ── Error ── */
  if (status === 'error') {
    return <div style={{
      background: '#FFF5F5',
      border: '1px solid #FED7D7',
      borderRadius: 12,
      padding: 20,
      textAlign: 'center'
    }}>
        <p style={{
        fontSize: 14,
        fontWeight: 600,
        color: '#C53030',
        fontFamily: "'Inter Tight',sans-serif",
        marginBottom: 6
      }}>
          {t("gated.form_temporarily_unavailable")}
        </p>
        <p style={{
        fontSize: 13,
        color: '#718096',
        fontFamily: "'Inter Tight',sans-serif",
        marginBottom: 14
      }}>
          {t("gated.our_team_has_been_notified_please_e")}
        </p>
        <a href="mailto:sales@locobuzz.com?subject=Resource Request" style={{
        display: 'inline-flex',
        padding: '9px 20px',
        borderRadius: 100,
        background: '#0098FF',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        textDecoration: 'none',
        fontFamily: "'Inter Tight',sans-serif"
      }}>
          {t("gated.email_us")}
        </a>
      </div>;
  }

  /* ── Loading + HubSpot form ── */
  return <div ref={wrapRef} className={styles.wrap} style={{
    position: 'relative',
    minHeight: 200
  }}>
      {status === 'loading' && <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 10,
      zIndex: 1
    }}>
          <div style={{
        width: 24,
        height: 24,
        border: '3px solid #E8ECF4',
        borderTopColor: '#0098FF',
        borderRadius: '50%',
        animation: 'hs-spin 0.8s linear infinite'
      }} />
          <p style={{
        fontSize: 12,
        color: '#9CA3AF',
        fontFamily: "'Inter Tight',sans-serif"
      }}>{t("gated.loading_form")}</p>
          <InlineStyle css={`@keyframes hs-spin{to{transform:rotate(360deg)}}`} />
        </div>}
      {/* The HubSpot form renders here — inside the card */}
      <div id={formId_ref.current} ref={divRef} style={{
      opacity: status === 'loading' ? 0 : 1,
      transition: 'opacity 0.3s',
      width: '100%'
    }} />
    </div>;
}